import { useState, useEffect } from 'react';
import {
  Button, TextField, Dialog, DialogActions, DialogContent, 
  DialogTitle, CircularProgress, Alert
} from '@mui/material';
import { showSuccessMessage } from '@/components/common/GlobalSnackbar.tsx';
import { apiClient } from '@/services/api';

// TypeScript 接口定义
interface User {
  id: number;
  username: string;
  display_name?: string;
  role: string;
  org_id?: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id?: number;
  report_template_id?: number;
  client_level?: string;
  evaluation_type?: string;
  status: string;
  contract_year?: number;
  dify_dataset_id?: string;
  created_at: string;
  updated_at: string;
}

interface CreateDepartmentTargetDialogProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
  editMode?: boolean;
  projectDataToEdit?: Project | null;
}

export default function CreateDepartmentTargetDialog({
  open,
  onClose,
  onProjectCreated,
  editMode = false,
  projectDataToEdit = null,
}: CreateDepartmentTargetDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 配额检查相关状态
  const [checkingQuota, setCheckingQuota] = useState(false);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [canCreateProject, setCanCreateProject] = useState(true);

  // Form states - 部门整体目标需要的字段
  const [projectName, setProjectName] = useState('');
  const [contractYear, setContractYear] = useState('');
  const [departmentName, setDepartmentName] = useState('');

  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
      setQuotaError(null);
      setCanCreateProject(true);

      // 如果是创建模式，先检查配额
      if (!editMode) {
        (async () => {
          setCheckingQuota(true);
          try {
            // 获取当前用户信息
            const userResp = await apiClient.get('/auth/session');
            if (userResp?.success && userResp.data?.id) {
              const userId = userResp.data.id;
              
              // 检查项目配额
              const quotaResp = await apiClient.get(`/auth/check-project-quota?userId=${userId}&projectType=department_overall`);
              
              if (!quotaResp?.success || !quotaResp.data?.canCreate) {
                setCanCreateProject(false);
                setQuotaError(quotaResp?.message || '无法创建项目，请先购买套餐');
              } else {
                setCanCreateProject(true);
                setQuotaError(null);
              }
            }
          } catch (err: any) {
            setQuotaError('检查配额失败，请稍后重试');
            setCanCreateProject(false);
          } finally {
            setCheckingQuota(false);
          }
        })();
      }

      if (editMode && projectDataToEdit) {
        // --- 编辑模式 ---
        setProjectName(projectDataToEdit.project_name || '');
        setContractYear(projectDataToEdit.contract_year?.toString() || new Date().getFullYear().toString());
        // 优先从项目本身回显；若没有则从 performance-meta 获取
        setDepartmentName((projectDataToEdit as any).department_name || '');

        // 补充：拉取 performance-meta 以确保部门（单位）名称能正确回显
        (async () => {
          try {
            const metaResp = await apiClient.get(`/projects/${projectDataToEdit.id}/performance-meta`);
            if (metaResp?.success && metaResp.data) {
              if (!((projectDataToEdit as any).department_name)) {
                setDepartmentName(metaResp.data.department_name || '');
              }
            }
          } catch (_) {
            // 忽略回显错误，避免阻塞编辑
          }
        })();
      } else {
        // --- 创建模式 ---
        setProjectName('');
        setContractYear(new Date().getFullYear().toString());
        setDepartmentName('');
      }
    }
  }, [open, editMode, projectDataToEdit]);

  // 合同年度验证函数
  const isValidContractYear = (yearStr: string): boolean => {
    if (!yearStr || !yearStr.trim()) return true; // 可选字段
    
    const yearNum = parseInt(yearStr);
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 10;
    const maxYear = currentYear + 10;
    
    return yearNum >= minYear && yearNum <= maxYear;
  };

  const validateForm = (): boolean => {
    if (!projectName.trim()) {
      setError('项目名称是必填项。');
      return false;
    }
    if (!contractYear.trim()) { setError('年度为必填项。'); return false; }
    if (!departmentName.trim()) { setError('部门（单位）名称为必填项。'); return false; }
    
    // 验证合同年度
    if (contractYear && contractYear.trim()) {
      const currentYear = new Date().getFullYear();
      const year = parseInt(contractYear);
      
      if (isNaN(year)) {
        setError('合同年度必须是有效的数字。');
        return false;
      }
      
      const minYear = currentYear - 10;
      const maxYear = currentYear + 10;
      
      if (year < minYear || year > maxYear) {
        setError(`合同年度必须在 ${minYear} 到 ${maxYear} 年之间。`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    // --- 创建时的默认值 ---
    const creationDefaults = {
      status: "new",
      industry_type: "",
      evaluation_type: "",
    };

    // --- 最终提交的数据 ---
    const projectPayload = {
      project_name: projectName,
      contract_year: contractYear.trim() || new Date().getFullYear().toString(),
      industry_type: "",
      department_name: departmentName,
      project_type: "department_overall", // 部门整体目标
    };
    
    try {
      if (editMode && projectDataToEdit?.id) {
        // 编辑模式：只发送可编辑的字段
        const response = await apiClient.put(`/projects/${projectDataToEdit.id}`, projectPayload);
        
        if (response.success) {
          showSuccessMessage('项目更新成功！');
        } else {
          throw new Error(response.message || '更新项目失败');
        }

      } else {
        // 创建模式：合并可编辑字段和默认值
        const finalPayload = { ...projectPayload, ...creationDefaults };
        const response = await apiClient.projects.create(finalPayload);

        if (!response.success) {
          // 后端已做配额检查，这里按消息触发购买弹窗
          const msg = response.message || '创建项目失败';
          if (/配额|额度|未开通|套餐/.test(msg)) {
            onClose();
            window.dispatchEvent(new CustomEvent('showPaymentDialog', {
              detail: {
                reason: 'quota_exceeded',
                message: msg
              }
            }));
            return;
          } else {
            throw new Error(msg);
          }
        } else {
          showSuccessMessage('部门整体目标创建成功！');
        }
      }

      // 成功后关闭对话框并刷新列表
      onClose();
      onProjectCreated();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editMode ? '编辑部门整体目标' : '创建部门整体目标'}
      </DialogTitle>
      <DialogContent>
        {/* 配额检查中的提示 */}
        {checkingQuota && !editMode && (
          <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            正在检查项目配额...
          </Alert>
        )}
        
        {/* 配额不足的提示 */}
        {!checkingQuota && quotaError && !editMode && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{quotaError}</strong>
            <br />
            请先购买套餐后再创建项目，避免录入数据后无法保存。
          </Alert>
        )}
        
        {/* 表单错误提示 */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          autoFocus
          margin="dense"
          label="项目名称 *"
          fullWidth
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          disabled={submitting || checkingQuota || !canCreateProject}
          sx={{ mb: 2 }}
        />
        <TextField
          label="年度 *"
          fullWidth
          margin="dense"
          variant="outlined"
          value={contractYear}
          onChange={(e) => setContractYear(e.target.value)}
          placeholder="例如：2024"
          error={!!(contractYear && contractYear.trim() && !isValidContractYear(contractYear))}
          helperText={contractYear && contractYear.trim() && !isValidContractYear(contractYear) ? 
            `合同年度必须在 ${new Date().getFullYear() - 10} 到 ${new Date().getFullYear() + 10} 年之间` : 
            '必填，格式如：2024'}
          disabled={submitting || checkingQuota || !canCreateProject}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="部门（单位）名称 *"
          fullWidth
          value={departmentName}
          onChange={(e) => setDepartmentName(e.target.value)}
          disabled={submitting || checkingQuota || !canCreateProject}
          sx={{ mb: 2 }}
        />

      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} color="secondary" disabled={submitting}>取消</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={submitting || checkingQuota || (!editMode && !canCreateProject)}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : (editMode ? '保存更改' : '创建')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

